/**
 * EntityMentionTooltip Component
 * Shows entity info when clicking a confirmed/known entity mention in the document
 */

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { getEntityConfig } from '@/types/document'
import { getEntityDomain, DOMAIN_CONFIG } from '@/lib/constants/entityDomainConfig'

interface EntityMentionInfo {
  entityType: string
  entityId?: string | null
  text: string
}

interface EntityMentionTooltipProps {
  entity: EntityMentionInfo | null
  position: { x: number; y: number } | null
  onClose: () => void
}

export function EntityMentionTooltip({
  entity,
  position,
  onClose,
}: EntityMentionTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!entity) return

    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [entity, onClose])

  if (!entity || !position) return null

  const config = getEntityConfig(entity.entityType)
  const domain = getEntityDomain(entity.entityType)
  const domainConfig = DOMAIN_CONFIG[domain]
  const isKnown = entity.entityId?.startsWith('persona-')
    || entity.entityId?.startsWith('competitor-')
    || entity.entityId?.startsWith('bmc-')
  const sourceLabel = entity.entityId?.startsWith('persona-')
    ? 'Persona Workshop'
    : entity.entityId?.startsWith('competitor-')
      ? 'Competitor Map'
      : entity.entityId?.startsWith('bmc-')
        ? 'Business Model Canvas'
        : 'Confirmed'

  return (
    <div
      ref={tooltipRef}
      className={cn(
        'fixed z-50 bg-white rounded-lg shadow-lg border border-warm-200',
        'animate-in fade-in-0 zoom-in-95 duration-150'
      )}
      style={{
        left: position.x,
        top: position.y + 8,
      }}
    >
      <div className="p-3 min-w-[160px] max-w-[280px]">
        {/* Entity text */}
        <div className="font-medium text-sm text-text-primary mb-1 line-clamp-2">
          {entity.text}
        </div>

        {/* Type with domain color */}
        <div className="flex items-center gap-2 text-xs mb-1">
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{
              backgroundColor: config.bgColor,
              color: config.color,
            }}
          >
            {config.icon} {config.label}
          </span>
          <span
            className="text-[10px] font-medium"
            style={{ color: domainConfig.color }}
          >
            {domain}
          </span>
        </div>

        {/* Source info */}
        {isKnown && (
          <div className="text-[10px] text-text-muted mt-1.5 pt-1.5 border-t border-warm-100">
            Known from {sourceLabel}
          </div>
        )}
      </div>
    </div>
  )
}
